import { useCallback } from 'react';
import { useReassignScheduleEntry } from '../../hooks/useReassignScheduleEntry';
import { useRescheduleScheduleEntry } from '../../hooks/useRescheduleScheduleEntry';
import { lookupErrorCopy } from '../../lib/errorMap';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';
import { instantToBusinessParts, localWallClockToInstant } from '../../components/schedule/scheduleHelpers';

function genIdempotencyKey(): string {
  return crypto.randomUUID();
}

/**
 * Drag-drop handlers wired to canonical Schedule RPCs. Reassign and reschedule
 * RPC bodies dual-write source tables (work_orders / bookings) and queue
 * pending_notifications transactionally — caller no longer dual-writes.
 */
export function useScheduleDragDrop(
  businessId: string | undefined,
  refresh: () => void,
  onError?: (message: string) => void,
) {
  const reassignEntry = useReassignScheduleEntry();
  const rescheduleEntry = useRescheduleScheduleEntry();

  // Reassign entry to a different person; optionally also reschedule if newStartAt
  // and newEndAt are provided (WeekView "drop on different day + different user").
  const handleEntryDrop = useCallback(async (
    entryId: string,
    newAssignedTo: string,
    _userId: string,
    newStartAt?: string,
    newEndAt?: string,
  ): Promise<boolean> => {
    if (!businessId) return false;

    const reassignKey = genIdempotencyKey();
    const reassignRes = await reassignEntry({
      entryId,
      newAssigneeId: newAssignedTo,
      idempotencyKey: reassignKey,
    });
    if (!reassignRes.success) {
      onError?.(lookupErrorCopy('reassign_schedule_entry', reassignRes.error.code));
      return false;
    }

    if (newStartAt && newEndAt) {
      const rescheduleRes = await rescheduleEntry({
        entryId,
        newStartAt,
        newEndAt,
        idempotencyKey: genIdempotencyKey(),
      });
      if (!rescheduleRes.success) {
        onError?.(lookupErrorCopy('reschedule_schedule_entry', rescheduleRes.error.code));
        return false;
      }
    }

    refresh();
    return true;
  }, [businessId, refresh, reassignEntry, rescheduleEntry, onError]);

  // Move entry to a different date (WeekView) — preserves time, changes date.
  const handleDateDrop = useCallback(async (
    entryId: string,
    newDate: string,
    _userId: string,
  ): Promise<boolean> => {
    if (!businessId) return false;

    const { data: entry } = await supabase.from('schedule_entries')
      .select('start_at, end_at').eq('id', entryId).maybeSingle();
    if (!entry) return false;

    // A2 P4 — start_at/end_at are true instants post-P1/P3. Read the wall-clock in
    // the business tz, keep that time-of-day, move it to newDate, then write true
    // instants via localWallClockToInstant (the P2 write helper). The old code
    // string-split the raw UTC component (wrong once storage became a true instant).
    const tz = useAuthStore.getState().business?.timezone ?? null;
    const s = instantToBusinessParts(entry.start_at, tz);
    const e = instantToBusinessParts(entry.end_at, tz);
    const pad = (n: number) => String(n).padStart(2, '0');
    const newStartAt = localWallClockToInstant(newDate, `${pad(s.hour)}:${pad(s.minute)}:00`, tz);
    const newEndAt = localWallClockToInstant(newDate, `${pad(e.hour)}:${pad(e.minute)}:00`, tz);

    const res = await rescheduleEntry({
      entryId,
      newStartAt,
      newEndAt,
      idempotencyKey: genIdempotencyKey(),
    });
    if (!res.success) {
      onError?.(lookupErrorCopy('reschedule_schedule_entry', res.error.code));
      return false;
    }
    refresh();
    return true;
  }, [businessId, refresh, rescheduleEntry, onError]);

  // Shift entry time by deltaMinutes (horizontal drag in TeamView).
  const handleTimeDrop = useCallback(async (
    entryId: string,
    deltaMinutes: number,
    _userId: string,
  ): Promise<boolean> => {
    if (!businessId || deltaMinutes === 0) return false;

    const { data: entry } = await supabase.from('schedule_entries')
      .select('start_at, end_at').eq('id', entryId).maybeSingle();
    if (!entry) return false;

    const oldStart = new Date(entry.start_at);
    const oldEnd = new Date(entry.end_at);
    const newStartAt = new Date(oldStart.getTime() + deltaMinutes * 60000).toISOString();
    const newEndAt = new Date(oldEnd.getTime() + deltaMinutes * 60000).toISOString();

    const res = await rescheduleEntry({
      entryId,
      newStartAt,
      newEndAt,
      idempotencyKey: genIdempotencyKey(),
    });
    if (!res.success) {
      onError?.(lookupErrorCopy('reschedule_schedule_entry', res.error.code));
      return false;
    }
    refresh();
    return true;
  }, [businessId, refresh, rescheduleEntry, onError]);

  return { handleEntryDrop, handleDateDrop, handleTimeDrop };
}
